import { useMemo, useRef, useEffect, useState } from 'react';
import useStaticProductsData from './useStaticProductsData';
import addDerivedProductData from '../useProductListingData/helpers/addDerivedProductData';

const useInfiniteStaticProducts = ({ productIds: allProductIds }) => {
  const fetchedIdsRef = useRef(new Set());
  const previousProductIdsRef = useRef([]);

  const [accumulatedProducts, setAccumulatedProducts] = useState([]);
  const [
    accumulatedUpholsteryOptions,
    setAccumulatedUpholsteryOptions,
  ] = useState([]);

  useEffect(() => {
    const currentIds = allProductIds || [];
    const previousIds = previousProductIdsRef.current || [];

    const firstIdChanged =
      previousIds.length > 0 &&
      currentIds.length > 0 &&
      String(previousIds[0]) !== String(currentIds[0]);

    if (firstIdChanged) {
      fetchedIdsRef.current = new Set();
      setAccumulatedProducts([]);
      setAccumulatedUpholsteryOptions([]);
    }

    previousProductIdsRef.current = currentIds;
  }, [allProductIds]);

  const newProductIds = useMemo(() => {
    if (!allProductIds?.length) return [];

    const fetchedNormalized = Array.from(fetchedIdsRef.current).map(id =>
      String(id)
    );
    return allProductIds.filter(id => !fetchedNormalized.includes(String(id)));
  }, [allProductIds]);

  const shouldFetch = newProductIds.length > 0;

  const currentData = useStaticProductsData(
    {
      productIds: newProductIds,
    },
    {
      skip: !shouldFetch,
    }
  );

  const newProductIdsRef = useRef(newProductIds);
  newProductIdsRef.current = newProductIds;

  useEffect(() => {
    const products = currentData?.products;
    const upholsteryOptions = currentData?.upholsteryOptions;
    const isLoading = currentData?.loading;
    const newIds = newProductIdsRef.current;

    if (!products && !isLoading) return;

    if (products?.length > 0) {
      newIds.forEach(id => fetchedIdsRef.current.add(String(id)));

      setAccumulatedProducts(prev => {
        const existingIds = new Set(prev.map(p => String(p.id)));
        const newProducts = products.filter(
          p => !existingIds.has(String(p.id))
        );

        return [...prev, ...newProducts];
      });

      if (upholsteryOptions?.length > 0) {
        setAccumulatedUpholsteryOptions(prev => [
          ...prev,
          ...upholsteryOptions,
        ]);
      }
    }
  }, [
    currentData?.products,
    currentData?.upholsteryOptions,
    currentData?.loading,
  ]);

  const finalProducts = useMemo(() => {
    if (!allProductIds?.length || !accumulatedProducts?.length) {
      return accumulatedProducts;
    }

    const requestedIdsNormalized = new Set(allProductIds.map(id => String(id)));

    return accumulatedProducts.filter(product => {
      const productIdNormalized = String(product.id);
      return requestedIdsNormalized.has(productIdNormalized);
    });
  }, [accumulatedProducts, allProductIds]);

  const productsWithAdditionalData = useMemo(
    () =>
      finalProducts.map(product =>
        addDerivedProductData({
          product,
          upholsteryOptions: accumulatedUpholsteryOptions,
        })
      ),
    [accumulatedUpholsteryOptions, finalProducts]
  );

  return {
    products: productsWithAdditionalData ?? [],
    loading: currentData?.loading || false,
  };
};

export default useInfiniteStaticProducts;
